import { useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Camera, Printer, SlidersHorizontal, Pencil } from 'lucide-react'
import { api } from '../lib/api'
import { Card, CardContent, CardHeader } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Modal } from '../components/ui/Modal'
import { Badge } from '../components/ui/Badge'
import { MediaImage } from '../components/MediaImage'
import { usePermissions } from '../contexts/permissions'
import {
  ENTRY_MODE_LABEL, KIND_LABEL, MOVEMENT_LABEL, apiError, fmtDate, fmtDateTime, fmtMoney,
  printVolumeLabels, resizeImage,
  type IndustryTenant, type MaterialDetail, type MovementType, type VolumeStatus,
} from '../lib/industria'

const selectClass = 'h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/60'

const VOLUME_BADGE: Record<VolumeStatus, 'default' | 'success' | 'warning' | 'danger' | 'info'> = {
  fechado: 'info', aberto: 'warning', consumido: 'default', quarentena: 'danger',
}

const MOVEMENT_BADGE: Record<MovementType, 'default' | 'success' | 'warning' | 'danger' | 'info'> = {
  in: 'success', out: 'info', adjustment: 'default', loss: 'danger', return_to_owner: 'warning',
}

export function IndustriaMaterialDetailPage() {
  const { id = '' } = useParams()
  const queryClient = useQueryClient()
  const { can } = usePermissions()
  const canEdit = can('industria-materiais', 'edit')

  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [moveOpen, setMoveOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [printing, setPrinting] = useState(false)
  const photoInput = useRef<HTMLInputElement>(null)

  const { data: material, isLoading } = useQuery<MaterialDetail>({
    queryKey: ['industria', 'material', id],
    queryFn: async () => (await api.get(`/materials/${id}`)).data,
    enabled: !!id,
  })

  const { data: tenants = [] } = useQuery<IndustryTenant[]>({
    queryKey: ['industria', 'tenants'],
    queryFn: async () => (await api.get('/materials/tenants')).data,
  })

  const [move, setMove] = useState({
    type: 'adjustment' as MovementType,
    quantity: '',
    reason: '',
    volumeId: '',
    lotId: '',
    ownerTenantId: '',
  })

  const [edit, setEdit] = useState({ name: '', minQty: '', reorderQty: '', notes: '', active: true })

  const openVolumes = useMemo(
    () => (material?.volumes ?? []).filter(v => v.status !== 'consumido'),
    [material],
  )

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['industria', 'material', id] })
    queryClient.invalidateQueries({ queryKey: ['industria', 'materials'] })
  }

  const moveMutation = useMutation({
    mutationFn: async () => (await api.post('/material-movements', {
      materialId: id,
      type: move.type,
      quantity: Number(move.quantity),
      reason: move.reason.trim(),
      volumeId: move.volumeId || null,
      lotId: move.volumeId ? null : (move.lotId || null),
      ownerTenantId: move.ownerTenantId || null,
    })).data,
    onSuccess: () => {
      invalidate()
      setMoveOpen(false)
      setMove({ type: 'adjustment', quantity: '', reason: '', volumeId: '', lotId: '', ownerTenantId: '' })
    },
    onError: (e) => setError(apiError(e, 'Não foi possível lançar o movimento')),
  })

  const editMutation = useMutation({
    mutationFn: async () => (await api.patch(`/materials/${id}`, {
      name: edit.name.trim(),
      minQty: Number(edit.minQty || 0),
      reorderQty: edit.reorderQty ? Number(edit.reorderQty) : null,
      notes: edit.notes.trim() || null,
      active: edit.active,
    })).data,
    onSuccess: () => { invalidate(); setEditOpen(false) },
    onError: (e) => setError(apiError(e, 'Não foi possível salvar')),
  })

  const photoMutation = useMutation({
    mutationFn: async (file: File) => {
      const data = await resizeImage(file)
      const contentType = /^data:(image\/(?:jpeg|png|webp));/.exec(data)?.[1] ?? 'image/jpeg'
      return (await api.post('/media', {
        kind: 'material_photo', ownerType: 'material', ownerId: id, contentType, data,
      })).data
    },
    onSuccess: invalidate,
    onError: (e) => setError(apiError(e, 'Não foi possível enviar a foto')),
  })

  async function handlePrint(format: 'label' | 'a4') {
    setPrinting(true)
    setError(null)
    try {
      await printVolumeLabels([...selected], format)
    } catch (e) {
      setError(apiError(e, 'Não foi possível gerar as etiquetas'))
    } finally {
      setPrinting(false)
    }
  }

  function toggle(volumeId: string) {
    setSelected(s => {
      const n = new Set(s)
      n.has(volumeId) ? n.delete(volumeId) : n.add(volumeId)
      return n
    })
  }

  if (isLoading) {
    return <p className="py-16 text-center text-sm text-gray-400">Carregando...</p>
  }
  if (!material) {
    return <p className="py-16 text-center text-sm text-gray-400">Material não encontrado.</p>
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-4">
          <MediaImage mediaId={material.photoMediaId} className="h-16 w-16 shrink-0 rounded-xl" />
          <div>
            <Link to="/industria/materiais" className="mb-1 inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700">
              <ArrowLeft className="h-3 w-3" /> Materiais
            </Link>
            <h2 className="text-xl font-semibold text-gray-900">{material.name}</h2>
            <p className="font-mono text-sm text-gray-500">
              {material.code} · {KIND_LABEL[material.kind]} · {material.ownerName ?? 'WS (próprio)'}
              {material.productSku ? ` · SKU ${material.productSku}` : ''}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Badge variant={material.belowMin ? 'warning' : 'success'}>
                {material.quantity.toLocaleString('pt-BR')} {material.unit} em estoque
              </Badge>
              {material.minQty > 0 && (
                <span className="text-xs text-gray-500">mínimo {material.minQty.toLocaleString('pt-BR')}</span>
              )}
              {!material.lotControlled && <Badge>sem controle de lote</Badge>}
              {!material.active && <Badge variant="danger">inativo</Badge>}
              {material.avgCost !== undefined && (
                <span className="text-xs text-gray-500">custo médio {fmtMoney(material.avgCost)}</span>
              )}
            </div>
          </div>
        </div>

        {canEdit && (
          <div className="flex flex-wrap gap-2">
            <input
              ref={photoInput}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={e => {
                const file = e.target.files?.[0]
                if (file) photoMutation.mutate(file)
                e.target.value = ''
              }}
            />
            <Button variant="secondary" onClick={() => photoInput.current?.click()} loading={photoMutation.isPending}>
              <Camera className="h-4 w-4" /> Foto
            </Button>
            <Button variant="secondary" onClick={() => {
              setEdit({
                name: material.name,
                minQty: String(material.minQty),
                reorderQty: material.reorderQty ? String(material.reorderQty) : '',
                notes: material.notes ?? '',
                active: material.active,
              })
              setError(null)
              setEditOpen(true)
            }}>
              <Pencil className="h-4 w-4" /> Editar
            </Button>
            <Button onClick={() => { setError(null); setMoveOpen(true) }}>
              <SlidersHorizontal className="h-4 w-4" /> Lançar movimento
            </Button>
          </div>
        )}
      </div>

      {error && <p className="rounded-lg bg-red-50 px-4 py-2 text-sm text-red-700">{error}</p>}

      <div className="grid gap-5 lg:grid-cols-3">
        <Card>
          <CardHeader><span className="text-sm font-semibold text-gray-900">Saldo por dono</span></CardHeader>
          <CardContent className="p-0">
            {material.byOwner.length === 0 ? (
              <p className="px-5 py-6 text-center text-sm text-gray-400">Sem saldo registrado</p>
            ) : (
              <ul className="divide-y divide-gray-50">
                {material.byOwner.map(o => (
                  <li key={o.ownerTenantId ?? 'ws'} className="flex items-center justify-between px-5 py-2.5 text-sm">
                    <span className="text-gray-700">{o.ownerName ?? 'WS (próprio)'}</span>
                    <span className={`font-mono ${o.quantity < 0 ? 'font-semibold text-red-600' : 'text-gray-900'}`}>
                      {o.quantity.toLocaleString('pt-BR')} {material.unit}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader><span className="text-sm font-semibold text-gray-900">Fotos</span></CardHeader>
          <CardContent>
            {material.photos.length === 0 ? (
              <p className="py-4 text-center text-sm text-gray-400">
                Nenhuma foto — a do catálogo entra pelo botão Foto; as de avaria, no recebimento
              </p>
            ) : (
              <div className="flex flex-wrap gap-3">
                {material.photos.map(p => (
                  <MediaImage key={p.id} mediaId={p.id} className="h-24 w-24 rounded-lg border border-gray-100" />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {(material.suppliers ?? []).length > 0 && (
        <Card>
          <CardHeader>
            <span className="text-sm font-semibold text-gray-900">Fornecedores deste material</span>
            <span className="text-xs text-gray-500">quem já entregou, pelo que veio em cada lote</span>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-2.5 text-left font-medium">Fornecedor</th>
                    <th className="px-4 py-2.5 text-right font-medium">Lotes</th>
                    <th className="px-4 py-2.5 text-right font-medium">Total recebido</th>
                    <th className="px-4 py-2.5 text-right font-medium">Último custo</th>
                    <th className="px-4 py-2.5 text-right font-medium">Prazo</th>
                    <th className="px-4 py-2.5 text-left font-medium">Última entrega</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {material.suppliers.map(f => (
                    <tr key={f.supplierId ?? 'sem'}>
                      <td className="px-4 py-2.5 text-gray-900">
                        {f.supplierName ?? '—'}
                        {f.supplierId === material.defaultSupplierId && (
                          <Badge variant="info" className="ml-2">habitual</Badge>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-600">{f.lotes}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-900">
                        {f.recebido.toLocaleString('pt-BR')} <span className="text-xs text-gray-400">{material.unit}</span>
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-900">{fmtMoney(f.ultimoCusto)}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-600">
                        {f.leadTimeDays ? `${f.leadTimeDays}d` : '—'}
                      </td>
                      <td className="px-4 py-2.5 text-gray-500">{fmtDateTime(f.ultimaEntrada)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><span className="text-sm font-semibold text-gray-900">Lotes</span></CardHeader>
        <CardContent className="p-0">
          {material.lots.length === 0 ? (
            <p className="px-5 py-8 text-center text-sm text-gray-400">Nenhum lote recebido</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-2.5 text-left font-medium">Lote</th>
                    <th className="px-4 py-2.5 text-left font-medium">Entrada</th>
                    <th className="px-4 py-2.5 text-left font-medium">Dono</th>
                    <th className="px-4 py-2.5 text-left font-medium">Documento</th>
                    <th className="px-4 py-2.5 text-left font-medium">Validade</th>
                    <th className="px-4 py-2.5 text-right font-medium">Recebido</th>
                    <th className="px-4 py-2.5 text-right font-medium">Saldo</th>
                    <th className="px-4 py-2.5 text-left font-medium">Recebido em</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {material.lots.map(l => (
                    <tr key={l.id}>
                      <td className="px-4 py-2.5">
                        <span className="font-mono font-medium text-gray-900">{l.lotNumber}</span>
                        {l.supplierLot && (
                          <span className="block text-xs text-gray-400">fornecedor: {l.supplierLot}</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-gray-600">{ENTRY_MODE_LABEL[l.entryMode]}</td>
                      <td className="px-4 py-2.5 text-gray-600">{l.ownerName ?? 'WS (próprio)'}</td>
                      <td className="px-4 py-2.5 text-gray-600">
                        {l.docNumber ?? '—'}
                        {l.supplierName && <span className="block text-xs text-gray-400">{l.supplierName}</span>}
                      </td>
                      <td className="px-4 py-2.5 text-gray-600">{fmtDate(l.expiryDate)}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-600">
                        {l.qtyReceived.toLocaleString('pt-BR')}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-900">
                        {l.quantity.toLocaleString('pt-BR')}
                      </td>
                      <td className="px-4 py-2.5 text-gray-500">{fmtDateTime(l.receivedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <span className="text-sm font-semibold text-gray-900">
            Volumes <span className="font-normal text-gray-400">({openVolumes.length} em aberto)</span>
          </span>
          {canEdit && selected.size > 0 && (
            <div className="flex gap-2">
              <Button size="sm" variant="secondary" loading={printing} onClick={() => handlePrint('a4')}>
                <Printer className="h-4 w-4" /> A4
              </Button>
              <Button size="sm" loading={printing} onClick={() => handlePrint('label')}>
                <Printer className="h-4 w-4" /> Etiqueta ({selected.size})
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {material.volumes.length === 0 ? (
            <p className="px-5 py-8 text-center text-sm text-gray-400">Nenhum volume</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="w-10 px-4 py-2.5"></th>
                    <th className="px-4 py-2.5 text-left font-medium">Volume</th>
                    <th className="px-4 py-2.5 text-left font-medium">Lote</th>
                    <th className="px-4 py-2.5 text-left font-medium">Dono</th>
                    <th className="px-4 py-2.5 text-left font-medium">Local</th>
                    <th className="px-4 py-2.5 text-right font-medium">Atual / inicial</th>
                    <th className="px-4 py-2.5 text-left font-medium">Situação</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {material.volumes.map(v => (
                    <tr key={v.id} className="hover:bg-gray-50/60">
                      <td className="px-4 py-2.5">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-gray-300"
                          checked={selected.has(v.id)}
                          onChange={() => toggle(v.id)}
                        />
                      </td>
                      <td className="px-4 py-2.5 font-mono font-medium text-gray-900">{v.code}</td>
                      <td className="px-4 py-2.5 font-mono text-gray-600">{v.lotNumber ?? '—'}</td>
                      <td className="px-4 py-2.5 text-gray-600">{v.ownerName ?? 'WS (próprio)'}</td>
                      <td className="px-4 py-2.5 font-mono text-gray-600">{v.locationCode ?? '—'}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-900">
                        {v.qtyCurrent.toLocaleString('pt-BR')}
                        <span className="text-gray-400"> / {v.qtyInitial.toLocaleString('pt-BR')}</span>
                      </td>
                      <td className="px-4 py-2.5">
                        <Badge variant={VOLUME_BADGE[v.status]}>{v.status}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><span className="text-sm font-semibold text-gray-900">Movimentos</span></CardHeader>
        <CardContent className="p-0">
          {material.movements.length === 0 ? (
            <p className="px-5 py-8 text-center text-sm text-gray-400">Nenhum movimento</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-2.5 text-left font-medium">Quando</th>
                    <th className="px-4 py-2.5 text-left font-medium">Tipo</th>
                    <th className="px-4 py-2.5 text-right font-medium">Qtd</th>
                    <th className="px-4 py-2.5 text-left font-medium">Lote / volume</th>
                    <th className="px-4 py-2.5 text-left font-medium">Dono</th>
                    <th className="px-4 py-2.5 text-left font-medium">Motivo</th>
                    <th className="px-4 py-2.5 text-left font-medium">Quem</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {material.movements.map(m => (
                    <tr key={m.id}>
                      <td className="px-4 py-2.5 text-gray-500">{fmtDateTime(m.movedAt)}</td>
                      <td className="px-4 py-2.5">
                        <Badge variant={MOVEMENT_BADGE[m.type]}>{MOVEMENT_LABEL[m.type]}</Badge>
                      </td>
                      <td className={`px-4 py-2.5 text-right font-mono ${m.quantity < 0 ? 'text-red-600' : 'text-green-700'}`}>
                        {m.quantity > 0 ? '+' : ''}{m.quantity.toLocaleString('pt-BR')}
                      </td>
                      <td className="px-4 py-2.5 font-mono text-xs text-gray-600">
                        {m.lotNumber ?? '—'}
                        {m.volumeCode && <span className="block text-gray-400">{m.volumeCode}</span>}
                      </td>
                      <td className="px-4 py-2.5 text-gray-600">{m.ownerName ?? 'WS (próprio)'}</td>
                      <td className="px-4 py-2.5 text-gray-600">{m.notes ?? '—'}</td>
                      <td className="px-4 py-2.5 text-gray-500">{m.author ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Modal title="Lançar movimento" open={moveOpen} onClose={() => setMoveOpen(false)}>
        <form
          className="space-y-4"
          onSubmit={e => { e.preventDefault(); setError(null); moveMutation.mutate() }}
        >
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Tipo</label>
              <select
                className={selectClass}
                value={move.type}
                onChange={e => setMove({ ...move, type: e.target.value as MovementType })}
              >
                {Object.entries(MOVEMENT_LABEL).map(([k, label]) => (
                  <option key={k} value={k}>{label}</option>
                ))}
              </select>
            </div>
            <Input
              label="Quantidade"
              type="number"
              min={1}
              value={move.quantity}
              onChange={e => setMove({ ...move, quantity: e.target.value })}
              required
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Volume (opcional)</label>
            <select
              className={selectClass}
              value={move.volumeId}
              onChange={e => setMove({ ...move, volumeId: e.target.value })}
            >
              <option value="">Nenhum — lança direto no saldo</option>
              {openVolumes.map(v => (
                <option key={v.id} value={v.id}>
                  {v.code} · lote {v.lotNumber ?? '—'} · {v.qtyCurrent} {material.unit}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Com o volume, lote, dono e local vêm dele — é o caminho sem digitação.
            </p>
          </div>

          {!move.volumeId && material.lots.length > 0 && (
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Lote (opcional)</label>
              <select
                className={selectClass}
                value={move.lotId}
                onChange={e => setMove({ ...move, lotId: e.target.value })}
              >
                <option value="">Nenhum</option>
                {material.lots.map(l => (
                  <option key={l.id} value={l.id}>
                    {l.lotNumber} · {l.ownerName ?? 'WS'} · saldo {l.quantity}
                  </option>
                ))}
              </select>
            </div>
          )}

          {!move.volumeId && !material.ownerTenantId && (
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Dono</label>
              <select
                className={selectClass}
                value={move.ownerTenantId}
                onChange={e => setMove({ ...move, ownerTenantId: e.target.value })}
              >
                <option value="">WS (próprio)</option>
                {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          )}

          <Input
            label="Motivo"
            value={move.reason}
            onChange={e => setMove({ ...move, reason: e.target.value })}
            placeholder="vidro quebrado na conferência"
            required
          />

          {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setMoveOpen(false)}>Cancelar</Button>
            <Button type="submit" loading={moveMutation.isPending}>Lançar</Button>
          </div>
        </form>
      </Modal>

      <Modal title="Editar material" open={editOpen} onClose={() => setEditOpen(false)}>
        <form
          className="space-y-4"
          onSubmit={e => { e.preventDefault(); setError(null); editMutation.mutate() }}
        >
          <Input label="Nome" value={edit.name} onChange={e => setEdit({ ...edit, name: e.target.value })} required />
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Mínimo"
              type="number"
              min={0}
              value={edit.minQty}
              onChange={e => setEdit({ ...edit, minQty: e.target.value })}
            />
            <Input
              label="Lote de compra sugerido"
              type="number"
              min={1}
              value={edit.reorderQty}
              onChange={e => setEdit({ ...edit, reorderQty: e.target.value })}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Observações</label>
            <textarea
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/60"
              rows={2}
              value={edit.notes}
              onChange={e => setEdit({ ...edit, notes: e.target.value })}
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300"
              checked={edit.active}
              onChange={e => setEdit({ ...edit, active: e.target.checked })}
            />
            Ativo
          </label>

          {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setEditOpen(false)}>Cancelar</Button>
            <Button type="submit" loading={editMutation.isPending}>Salvar</Button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
